import { Trans, initI18nWithoutICU } from '@wix/wix-i18n-config';
import { TextButton } from '@wix/design-system';
import React from 'react';
import type { TermsAndConditionsProps } from './terms-and-conditions';
import { WIX_APP_MARKET_TERMS_AND_CONDITIONS } from './constants';
import { useEssentials } from '../../contexts/essentials-context';

export const useTermsAndConditionsText = ({
  darkCtas,
  appName,
  companyName,
  termsOfUseUrl,
  privacyPolicyUrl,
  isInstalled,
  isSelfPublished,
  onTermsAndConditionsClick,
  onPrivacyPolicyClick,
}: TermsAndConditionsProps) => {
  const { languageCode, t } = useEssentials();
  const [i18n] = React.useState(() =>
    initI18nWithoutICU({ locale: languageCode, messages: {} }),
  );
  const textButtonSkin = darkCtas ? 'dark' : 'standard';

  const WixTermsAndConditions = (
    <TextButton
      size="small"
      underline="always"
      skin={textButtonSkin}
      dataHook="wix-terms-and-conditions-button"
      onClick={() =>
        onTermsAndConditionsClick(WIX_APP_MARKET_TERMS_AND_CONDITIONS)
      }
    />
  );
  const TpaTermsAndConditions = (
    <TextButton
      size="small"
      underline="always"
      skin={textButtonSkin}
      dataHook="tpa-terms-and-conditions-button"
      onClick={() => onTermsAndConditionsClick(termsOfUseUrl ?? '')}
    />
  );
  const TpaPrivacyPolicy = privacyPolicyUrl ? (
    <TextButton
      size="small"
      underline="always"
      skin={textButtonSkin}
      dataHook="tpa-privacy-policy-button"
      onClick={() => onPrivacyPolicyClick(privacyPolicyUrl)}
    />
  ) : (
    <span />
  );

  const transValues = {
    appName: privacyPolicyUrl && !termsOfUseUrl ? companyName : appName,
  };

  if (isSelfPublished) {
    return (
      <Trans
        t={t}
        i18n={i18n}
        i18nKey="app-page.terms-and-conditions.self-published"
        values={transValues}
      >
        {WixTermsAndConditions}
        {TpaPrivacyPolicy}
      </Trans>
    );
  }

  if (termsOfUseUrl) {
    return (
      <Trans
        t={t}
        i18n={i18n}
        i18nKey={`app-page.terms-and-conditions.custom-terms-and-privacy.${
          isInstalled ? 'updating' : 'installing'
        }`}
        values={transValues}
      >
        {WixTermsAndConditions}
        {TpaTermsAndConditions}
        {TpaPrivacyPolicy}
      </Trans>
    );
  }

  return (
    <Trans
      dataHook="default-terms-and-conditions"
      t={t}
      i18n={i18n}
      i18nKey={`app-page.terms-and-conditions.default.${
        isInstalled ? 'updating' : 'installing'
      }`}
      values={transValues}
    >
      {WixTermsAndConditions}
      {TpaPrivacyPolicy}
    </Trans>
  );
};
